import { defineStore } from 'pinia';
import camelcaseKeys from 'camelcase-keys';
import snakecaseKeys from 'snakecase-keys';
import {
  addFavorite,
  dealsAPI,
  pipelinesAPI,
  removeFavorite,
  duplicatePipeline as duplicatePipelineApi,
} from './api';

// user-defined keys (custom attributes/fields, automation payloads) must keep their casing
const RAW_KEYS = [
  'customAttributes',
  'custom_attributes',
  'customFields',
  'custom_fields',
  'automations',
  'templateParams',
  'template_params',
];
export const camelize = data =>
  camelcaseKeys(data || {}, {
    deep: true,
    stopPaths: ['custom_attributes', 'custom_fields', 'stages.automations'],
  });
const snakeize = data =>
  snakecaseKeys(data || {}, { deep: true, exclude: RAW_KEYS });

// snakecase-keys still recurses into excluded keys' children; keep those subtrees verbatim.
const withRaw = (snaked, original) => {
  const out = { ...snaked };
  // the exclude list keeps these keys camelCase in `snaked`; drop the duplicates
  delete out.customAttributes;
  delete out.customFields;
  if (original.customAttributes !== undefined)
    out.custom_attributes = original.customAttributes;
  if (original.customFields !== undefined)
    out.custom_fields = original.customFields;
  if (original.stagesAttributes) {
    out.stages_attributes = original.stagesAttributes.map((stage, i) => ({
      ...out.stages_attributes[i],
      ...(stage.automations !== undefined
        ? { automations: stage.automations }
        : {}),
    }));
  }
  return out;
};

export const useCrmStore = defineStore('wasapiCrm', {
  state: () => ({
    pipelines: [],
    deals: [],
    activePipelineId: null,
    onlyFavorites: false,
    uiFlags: {
      fetchingPipelines: false,
      fetchingDeals: false,
      savingDeal: false,
      savingPipeline: false,
    },
  }),

  getters: {
    activePipeline: state =>
      state.pipelines.find(p => p.id === state.activePipelineId) || null,
    stages() {
      return this.activePipeline?.stages || [];
    },
    allStages: state => state.pipelines.flatMap(p => p.stages),
    dealsByStage: state =>
      state.deals.reduce((acc, deal) => {
        (acc[deal.stageId] ||= []).push(deal);
        return acc;
      }, {}),
  },

  actions: {
    async fetchPipelines() {
      this.uiFlags.fetchingPipelines = true;
      try {
        const { data } = await pipelinesAPI.get();
        this.pipelines = data.map(camelize);
        if (!this.activePipeline) {
          const fallback =
            this.pipelines.find(p => p.default) || this.pipelines[0];
          this.activePipelineId = fallback?.id || null;
        }
      } finally {
        this.uiFlags.fetchingPipelines = false;
      }
    },

    async setActivePipeline(id) {
      this.activePipelineId = id;
      await this.fetchDeals();
    },

    async fetchDeals() {
      if (!this.activePipelineId) return;
      // race guard: fast pipeline switches must not let a slow response win
      const requestedPipelineId = this.activePipelineId;
      this.uiFlags.fetchingDeals = true;
      try {
        const { data } = await dealsAPI.list({
          pipeline_id: requestedPipelineId,
          favorites: this.onlyFavorites || undefined,
        });
        if (this.activePipelineId !== requestedPipelineId) return;
        this.deals = data.map(camelize);
      } finally {
        if (this.activePipelineId === requestedPipelineId)
          this.uiFlags.fetchingDeals = false;
      }
    },

    upsertDeal(deal) {
      const index = this.deals.findIndex(d => d.id === deal.id);
      if (index === -1) this.deals.push(deal);
      else this.deals.splice(index, 1, deal);
    },

    async createDeal(payload) {
      this.uiFlags.savingDeal = true;
      try {
        const { data } = await dealsAPI.create({
          deal: withRaw(snakeize(payload), payload),
        });
        const deal = camelize(data);
        if (deal.pipelineId === this.activePipelineId) this.upsertDeal(deal);
        return deal;
      } finally {
        this.uiFlags.savingDeal = false;
      }
    },

    async updateDeal(id, payload) {
      this.uiFlags.savingDeal = true;
      try {
        const { data } = await dealsAPI.update(id, {
          deal: withRaw(snakeize(payload), payload),
        });
        const deal = camelize(data);
        this.upsertDeal(deal);
        return deal;
      } finally {
        this.uiFlags.savingDeal = false;
      }
    },

    async moveDeal(id, { stageId, position, closeReasonId }) {
      // optimistic update
      const deal = this.deals.find(d => d.id === id);
      if (deal) {
        deal.stageId = stageId;
        deal.position = position;
      }
      const { data } = await dealsAPI.move(id, {
        stageId,
        position,
        closeReasonId,
      });
      this.upsertDeal(camelize(data.deal));
      // the server renumbers whole columns: sync every sibling position
      (data.positions || []).forEach(row => {
        const sibling = this.deals.find(d => d.id === row.id);
        if (sibling) {
          sibling.stageId = row.stage_id;
          sibling.position = row.position;
        }
      });
    },

    async toggleOnlyFavorites() {
      this.onlyFavorites = !this.onlyFavorites;
      await this.fetchDeals();
    },

    async toggleFavorite(deal) {
      const next = !deal.isFavorite;
      if (next) await addFavorite('Wasapi::Deal', deal.id);
      else await removeFavorite('Wasapi::Deal', deal.id);
      const local = this.deals.find(d => d.id === deal.id);
      if (local) local.isFavorite = next;
      if (!next && this.onlyFavorites) {
        this.deals = this.deals.filter(d => d.id !== deal.id);
      }
    },

    async deleteDeal(id) {
      await dealsAPI.delete(id);
      this.deals = this.deals.filter(d => d.id !== id);
    },

    async duplicatePipeline(id) {
      const { data } = await duplicatePipelineApi(id);
      const pipeline = camelize(data);
      this.pipelines.push(pipeline);
      this.activePipelineId = pipeline.id;
      await this.fetchDeals();
      return pipeline;
    },

    async savePipeline(id, payload) {
      this.uiFlags.savingPipeline = true;
      try {
        const body = { pipeline: withRaw(snakeize(payload), payload) };
        const { data } = id
          ? await pipelinesAPI.update(id, body)
          : await pipelinesAPI.create(body);
        const pipeline = camelize(data);
        const index = this.pipelines.findIndex(p => p.id === pipeline.id);
        if (index === -1) this.pipelines.push(pipeline);
        else this.pipelines.splice(index, 1, pipeline);
        return pipeline;
      } finally {
        this.uiFlags.savingPipeline = false;
      }
    },

    async deletePipeline(id) {
      await pipelinesAPI.delete(id);
      this.pipelines = this.pipelines.filter(p => p.id !== id);
      if (this.activePipelineId === id) {
        this.activePipelineId = this.pipelines[0]?.id || null;
        await this.fetchDeals();
      }
    },
  },
});
